import { isCriterionIncomplete } from '@/common/components/ConditionsEditor/helpers'
import { isDelayDaysValid } from '@/common/components/DelayDaysInput/helpers'
import { refundHoldModeValues } from '@/common/constants'
import { isHtmlContentEmpty } from '@/common/helpers/helpers'

import { bonusAppliesToValues } from './ActionConfigs/ApplyBonusConfig/constants'
import { feeApplicationModeValues } from './ActionConfigs/ApplyExtraFeeConfig/constants'
import { triggerModeValues } from './ActionConfigs/constants'
import { consentModalTextFields } from './ActionConfigs/DisplayConsentModalConfig/constants'
import { productDialogPlacements } from './ActionConfigs/DisplayMessageConfig/constants'
import { isTotalBlock } from './BlockOptionsCheckboxes/helpers'
import { actionTypes, actionTypeValues } from './constants'

export const getAvailableActionTypeOptions = disabledActions => {
  return Object.entries(actionTypes)
    .filter(([value]) => !disabledActions.includes(value))
    .map(([value, { label, icon }]) => ({ value, label, icon }))
}

export const getDefaultConfigForAction = actionType => {
  const config = actionTypes[actionType]?.defaultConfig
  if (!config) return {}
  return { ...config }
}

const hasAtLeastOneBlockOption = config => {
  return config.block_returns || config.block_variant_exchanges || config.block_exchange_for_other
}

const getMissingCustomMessageLanguages = (actionConfig, supportedLanguages) => {
  return supportedLanguages.filter(lang => isHtmlContentEmpty(actionConfig.custom_message?.[lang]))
}

const getMissingTextLanguages = (texts, supportedLanguages) => {
  return supportedLanguages.filter(lang => !texts?.[lang]?.trim())
}

const isProductConditionComplete = config => {
  return config.product_criteria.length > 0 && !config.product_criteria.some(isCriterionIncomplete)
}

const getCustomMessageErrors = ({ actionConfig, errorTypes, supportedLanguages }) => {
  const missingLanguages = getMissingCustomMessageLanguages(actionConfig, supportedLanguages)

  if (!missingLanguages.length) return []

  return [{ type: errorTypes.actionCustomMessageEmpty, missingLanguages }]
}

const validateBlockOptions = ({ actionConfig, errorTypes }) => {
  const errors = []

  if (!hasAtLeastOneBlockOption(actionConfig)) {
    errors.push({ type: errorTypes.actionBlockOptionsEmpty })
  }

  if (actionConfig.custom_message_enabled && !isTotalBlock(actionConfig) && !actionConfig.custom_message_type) {
    errors.push({ type: errorTypes.actionCustomMessageTypeEmpty })
  }

  return errors
}

const validateBlockItemOptions = context => {
  const errors = validateBlockOptions(context)

  if (!isProductConditionComplete(context.actionConfig)) {
    errors.push({ type: context.errorTypes.actionProductConditionIncomplete })
  }

  return errors
}

const validateDisableInstant = ({ actionConfig, errorTypes }) => {
  if (actionConfig.disable_returns || actionConfig.disable_exchanges) return []

  return [{ type: errorTypes.actionDisableForEmpty }]
}

const validateExtendWindow = ({ actionConfig, errorTypes }) => {
  if (actionConfig.extend_until_date || actionConfig.extend_by_days) return []

  return [{ type: errorTypes.actionExtendConfigIncomplete }]
}

const validateHoldReason = ({ actionConfig, errorTypes }) => {
  if (actionConfig.hold_reason?.trim()) return []

  return [{ type: errorTypes.actionReasonEmpty }]
}

const validatePutRefundOnHold = context => {
  const { actionConfig, errorTypes } = context
  const errors = validateHoldReason(context)
  const delaysRefund = actionConfig.refund_hold_mode === refundHoldModeValues.delayByDays

  if (delaysRefund && !isDelayDaysValid(actionConfig.refund_hold_delay_days)) {
    errors.push({ type: errorTypes.actionDelayDaysInvalid })
  }

  return errors
}

const hasAtLeastOneBonusOption = config => {
  return (
    config.bonus_free_return ||
    config.bonus_free_exchange ||
    config.bonus_refund_shipping ||
    config.bonus_exchange_discount
  )
}

const getBonusDiscountErrors = ({ actionConfig, errorTypes }) => {
  const errors = []
  const appliesToNewRequestedProducts =
    actionConfig.bonus_exchange_discount_applies_to === bonusAppliesToValues.newRequestedProducts

  if (!actionConfig.bonus_exchange_discount_type) {
    errors.push({ type: errorTypes.actionBonusDiscountTypeEmpty })
  }

  if (!actionConfig.bonus_exchange_discount_value) {
    errors.push({ type: errorTypes.actionBonusDiscountValueEmpty })
  }

  if (!actionConfig.bonus_exchange_discount_label.trim()) {
    errors.push({ type: errorTypes.actionBonusDiscountLabelEmpty })
  }

  if (appliesToNewRequestedProducts && !isProductConditionComplete(actionConfig)) {
    errors.push({ type: errorTypes.actionProductConditionIncomplete })
  }

  return errors
}

const validateApplyBonus = context => {
  const { actionConfig, errorTypes } = context

  if (!hasAtLeastOneBonusOption(actionConfig)) return [{ type: errorTypes.actionBonusOptionsEmpty }]
  if (!actionConfig.bonus_exchange_discount) return []

  return getBonusDiscountErrors(context)
}

const validateApplyExtraFee = ({ actionConfig, errorTypes }) => {
  const errors = []
  const isPerMatchingProduct = actionConfig.fee_application_mode === feeApplicationModeValues.perMatchingProduct

  if (!actionConfig.fee_concept.trim()) {
    errors.push({ type: errorTypes.actionFeeNameEmpty })
  }

  if (!actionConfig.fee_amount) {
    errors.push({ type: errorTypes.actionFeeAmountEmpty })
  }

  if (!actionConfig.fee_currency) {
    errors.push({ type: errorTypes.actionFeeCurrencyEmpty })
  }

  if (isPerMatchingProduct && !isProductConditionComplete(actionConfig)) {
    errors.push({ type: errorTypes.actionProductConditionIncomplete })
  }

  if (actionConfig.custom_message_enabled && !actionConfig.custom_message_type) {
    errors.push({ type: errorTypes.actionCustomMessageTypeEmpty })
  }

  return errors
}

const validateForceRefundMethod = ({ actionConfig, errorTypes }) => {
  if (actionConfig.refund_method) return []

  return [{ type: errorTypes.actionRefundMethodEmpty }]
}

const validateForceRefundTrigger = ({ actionConfig, errorTypes }) => {
  const errors = []
  const delaysTrigger = actionConfig.refund_trigger_mode === triggerModeValues.delayByDays

  if (!actionConfig.refund_trigger_status) {
    errors.push({ type: errorTypes.actionRefundTriggerEmpty })
  }

  if (delaysTrigger && !isDelayDaysValid(actionConfig.refund_trigger_delay_days)) {
    errors.push({ type: errorTypes.actionDelayDaysInvalid })
  }

  return errors
}

const validateForceExchangeOrderTrigger = ({ actionConfig, errorTypes }) => {
  const errors = []
  const delaysTrigger = actionConfig.exchange_trigger_mode === triggerModeValues.delayByDays

  if (!actionConfig.exchange_trigger_status) {
    errors.push({ type: errorTypes.actionExchangeTriggerEmpty })
  }

  if (delaysTrigger && !isDelayDaysValid(actionConfig.exchange_trigger_delay_days)) {
    errors.push({ type: errorTypes.actionDelayDaysInvalid })
  }

  return errors
}

const validateDisableRefundMethods = ({ actionConfig, errorTypes }) => {
  if (actionConfig.refund_methods.length) return []

  return [{ type: errorTypes.actionDisableRefundMethodsEmpty }]
}

const validateAddTags = ({ actionConfig, errorTypes }) => {
  if (actionConfig.tags?.length) return []

  return [{ type: errorTypes.actionTagsEmpty }]
}

const validateDisplayMessage = context => {
  const { actionConfig, errorTypes } = context
  const errors = getCustomMessageErrors(context)
  const isProductDialogPlacement = productDialogPlacements.includes(actionConfig.display_message_placement)

  if (!actionConfig.custom_message_type) {
    errors.push({ type: errorTypes.actionCustomMessageTypeEmpty })
  }

  if (!actionConfig.display_message_placement) {
    errors.push({ type: errorTypes.actionMessagePlacementEmpty })
  }

  if (isProductDialogPlacement && !isProductConditionComplete(actionConfig)) {
    errors.push({ type: errorTypes.actionProductConditionIncomplete })
  }

  return errors
}

const validateDisplayConsentModal = ({ actionConfig, errorTypes, supportedLanguages }) => {
  const errors = consentModalTextFields
    .map(({ key, errorType }) => {
      return { type: errorType, missingLanguages: getMissingTextLanguages(actionConfig[key], supportedLanguages) }
    })
    .filter(error => error.missingLanguages.length > 0)

  if (!actionConfig.display_message_placement) {
    errors.push({ type: errorTypes.actionMessagePlacementEmpty })
  }

  return errors
}

const actionConfigValidators = {
  [actionTypeValues.blockOrderOptions]: validateBlockOptions,
  [actionTypeValues.blockItemOptions]: validateBlockItemOptions,
  [actionTypeValues.disableInstant]: validateDisableInstant,
  [actionTypeValues.extendWindow]: validateExtendWindow,
  [actionTypeValues.putOnHold]: validateHoldReason,
  [actionTypeValues.putRefundOnHold]: validatePutRefundOnHold,
  [actionTypeValues.applyBonus]: validateApplyBonus,
  [actionTypeValues.applyExtraFee]: validateApplyExtraFee,
  [actionTypeValues.forceRefundMethod]: validateForceRefundMethod,
  [actionTypeValues.forceRefundTrigger]: validateForceRefundTrigger,
  [actionTypeValues.forceExchangeOrderTrigger]: validateForceExchangeOrderTrigger,
  [actionTypeValues.disableRefundMethods]: validateDisableRefundMethods,
  [actionTypeValues.addTags]: validateAddTags,
  [actionTypeValues.displayMessage]: validateDisplayMessage,
  [actionTypeValues.displayConsentModal]: validateDisplayConsentModal,
}

export const validateActionConfig = (actionType, actionConfig, errorTypes, supportedLanguages) => {
  const context = { actionConfig, errorTypes, supportedLanguages }
  const customMessageErrors = actionConfig.custom_message_enabled ? getCustomMessageErrors(context) : []
  const validateAction = actionConfigValidators[actionType]

  if (!validateAction) return customMessageErrors

  return [...customMessageErrors, ...validateAction(context)]
}
